import { imhubInstance } from "../../config/index";

const initialState = () => ({
  // Context
  currentDialogId: null,
  currentInsights: null,

  // Connection
  authFailedImhub: false,
  invalidStreamingToken: false,
  connectionOptions: null,
  wsStatus: "CLOSED",
  wsData: null,
  wsConnected: false,
  wsInstance: null,
  pendingConnectionResolve: null,
  pendingConnectionReject: null,
  isRequestingToken: false,
  isConnecting: false,

  // Messages
  allSessionMessages: [],
  debuggingMessages: [],
  insightMessages: [],
});

const getters = {
  authFailedImhub: (state) => state.authFailedImhub,

  invalidStreamingToken: (state) => state.invalidStreamingToken,

  isConnected: (state) => state.wsConnected,

  isRequestingToken: (state) => state.isRequestingToken,

  isConnecting: (state) => state.isConnecting,

  status: (state) => state.wsStatus,

  currentInsights: (state) => state.currentInsights,

  allSessionMessages: (state) => state.allSessionMessages,

  debuggingMessages: (state) => state.debuggingMessages,

  insightMessages: (state) => state.insightMessages,

  lastSessionMessage: (state) => {
    return state.allSessionMessages.length > 0
      ? state.allSessionMessages[state.allSessionMessages.length - 1]
      : null;
  },

  lastDebuggingMessage: (state) => {
    return state.debuggingMessages.length > 0
      ? state.debuggingMessages[state.debuggingMessages.length - 1]
      : null;
  },

  lastInsightMessage: (state) => {
    return state.insightMessages.length > 0
      ? state.insightMessages[state.insightMessages.length - 1]
      : null;
  },

  statusClass: (state) => ({
    connected: state.wsStatus === "OPEN" && state.wsConnected,
    connecting: state.wsStatus === "CONNECTING",
    disconnected:
      !state.wsConnected &&
      (state.wsStatus === "CLOSED" || state.wsStatus === "CLOSING"),
  }),

  statusText: (state) => {
    if (state.wsConnected) return "🟢 Conectado";
    if (state.wsStatus === "CONNECTING") return "🟡 Conectando...";

    return "🔴 Desconectado";
  },

  currentContext: (state) => ({
    dialogId: state.currentDialogId,
  }),
};

const mutations = {
  SET_AUTH_FAILED_IMHUB(state, boolean) {
    state.authFailedImhub = boolean;
  },

  SET_INVALID_STREAMING_TOKEN(state, boolean) {
    state.invalidStreamingToken = boolean;
  },

  SET_IS_REQUESTING_TOKEN(state, boolean) {
    state.isRequestingToken = boolean;
  },

  SET_IS_CONNECTING(state, boolean) {
    state.isConnecting = boolean;
  },

  SET_WS_STATUS(state, status) {
    state.wsStatus = status;
  },

  SET_WS_DATA(state, data) {
    state.wsData = data;
  },

  SET_WS_CONNECTED(state, connected) {
    state.wsConnected = connected;
  },

  SET_WS_INSTANCE(state, { ws }) {
    state.wsInstance = ws;
  },

  SET_PENDING_CONNECTION_HANDLERS(state, { resolve, reject }) {
    state.pendingConnectionResolve = resolve;
    state.pendingConnectionReject = reject;
  },

  CLEAR_PENDING_CONNECTION_HANDLERS(state) {
    state.pendingConnectionResolve = null;
    state.pendingConnectionReject = null;
  },

  SET_CURRENT_CONTEXT(state, { dialogId }) {
    state.currentDialogId = dialogId;
  },

  SET_CONNECTION_OPTIONS(state, options) {
    state.connectionOptions = options;
  },

  SET_CURRENT_INSIGHTS(state, insights) {
    state.currentInsights = insights;
  },

  ADD_MESSAGE(state, message) {
    state.allSessionMessages.push({
      ...message,
      timestamp: message.timestamp || Date.now(),
    });
  },

  ADD_DEBUGGING_MESSAGE(state, message) {
    state.debuggingMessages.push({
      ...message,
      timestamp: message.timestamp || Date.now(),
    });
  },

  ADD_INSIGHT_MESSAGE(state, message) {
    state.insightMessages.push({
      ...message,
      timestamp: message.timestamp || Date.now(),
    });
  },

  CLEAR_CONTEXT(state) {
    state.currentDialogId = null;
    state.currentInsights = null;
  },

  CLEAR_ALL_MESSAGES(state) {
    state.allSessionMessages = [];
    state.debuggingMessages = [];
    state.insightMessages = [];
  },

  CLEAR_ALL_SESSION_MESSAGES(state) {
    state.allSessionMessages = [];
  },

  CLEAR_DEBUGGING_MESSAGES(state) {
    state.debuggingMessages = [];
  },

  CLEAR_INSIGHT_MESSAGES(state) {
    state.insightMessages = [];
  },

  RESET_STATE(state) {
    const initial = initialState();

    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const actions = {
  requestStreamingToken({ commit, state }, data) {
    if (state.isRequestingToken) {
      return Promise.reject(new Error("Token request already in progress"));
    }

    commit("SET_IS_REQUESTING_TOKEN", true);

    const token =
      process.env.NODE_ENV == "production"
        ? localStorage.getItem("token")
        : localStorage.getItem("tokenProdOnStg");

    const postUrl = `/security/crm/streaming-token`;

    const body = {
      token: token,
      username: data.username,
    };

    return new Promise((resolve, reject) => {
      imhubInstance()
        .post(postUrl, body)
        .then((response) => {
          commit("SET_AUTH_FAILED_IMHUB", false);
          commit("SET_INVALID_STREAMING_TOKEN", false);

          localStorage.setItem("streamingToken", response.streaming_token);
          localStorage.setItem("hubUsername", response.username);

          resolve(response);
        })
        .catch((err) => {
          commit("SET_AUTH_FAILED_IMHUB", true);

          reject(err);
        })
        .finally(() => {
          commit("SET_IS_REQUESTING_TOKEN", false);
        });
    });
  },

  connect({ commit, dispatch, state }, options = {}) {
    return new Promise((resolve, reject) => {
      if (state.isConnecting) {
        reject(new Error("Connection already in progress"));
        return;
      }

      if (state.wsConnected && state.wsStatus === "OPEN") {
        resolve({
          ws: state.wsInstance,
          fromRequestInsights: options.fromRequestInsights,
        });
        return;
      }

      const websocketUrl = options.url || getWebSocketUrl();

      if (!websocketUrl) {
        reject(new Error("No WebSocket URL available"));
        return;
      }

      const fromRequestInsights = options.fromRequestInsights || false;

      delete options.fromRequestInsights;

      commit("SET_IS_CONNECTING", true);
      commit("SET_WS_STATUS", "CONNECTING");
      commit("SET_CONNECTION_OPTIONS", options);
      commit("SET_PENDING_CONNECTION_HANDLERS", { resolve, reject });

      try {
        const ws = new WebSocket(websocketUrl);
        let isValidated = false;

        ws.onopen = () => {
          commit("SET_WS_CONNECTED", true);
          commit("SET_WS_STATUS", "OPEN");

          if (!options.onConnected) {
            dispatch("handleConnected", ws);
          } else {
            options.onConnected(ws);
          }
        };

        ws.onclose = (event) => {
          commit("SET_WS_CONNECTED", false);
          commit("SET_WS_STATUS", "CLOSED");
          commit("SET_IS_CONNECTING", false);

          if (!options.onDisconnected) {
            dispatch("handleDisconnected", { websocket: ws, event });
          } else {
            options.onDisconnected(ws, event);
          }

          if (!isValidated && state.pendingConnectionReject) {
            const errorMessage =
              event?.reason || "Connection closed before validation";
            state.pendingConnectionReject(new Error(errorMessage));
            commit("CLEAR_PENDING_CONNECTION_HANDLERS");
          }
        };

        ws.onerror = (event) => {
          commit("SET_IS_CONNECTING", false);

          if (!options.onError) {
            dispatch("handleError", { websocket: ws, event });
          } else {
            options.onError(ws, event);
          }

          if (!isValidated && state.pendingConnectionReject) {
            state.pendingConnectionReject(new Error("WebSocket error"));
            commit("CLEAR_PENDING_CONNECTION_HANDLERS");
          }
        };

        ws.onmessage = (event) => {
          let parsedData = event.data;

          try {
            parsedData = JSON.parse(event.data);
          } catch (e) {
            return;
          }

          commit("SET_WS_DATA", parsedData);

          if (!isValidated && state.pendingConnectionResolve) {
            isValidated = true;
            commit("SET_IS_CONNECTING", false);
            state.pendingConnectionResolve({ ws, fromRequestInsights });
            commit("CLEAR_PENDING_CONNECTION_HANDLERS");
          }

          if (!options.onMessage) {
            dispatch("handleMessage", { data: parsedData, event });
          } else {
            options.onMessage(ws, event);
          }
        };

        commit("SET_WS_INSTANCE", { ws });

        setTimeout(() => {
          if (!isValidated && state.pendingConnectionResolve) {
            isValidated = true;
            commit("SET_IS_CONNECTING", false);

            if (state.wsConnected) {
              state.pendingConnectionResolve({ ws, fromRequestInsights });
            } else {
              state.pendingConnectionReject(new Error("Connection timeout"));
            }
            commit("CLEAR_PENDING_CONNECTION_HANDLERS");
          }
        }, 30000);
      } catch (error) {
        commit("SET_WS_STATUS", "CLOSED");
        commit("SET_WS_CONNECTED", false);
        commit("SET_IS_CONNECTING", false);

        commit("ADD_DEBUGGING_MESSAGE", {
          type: "websocket-connection-error-debug",
          data: error,
          timestamp: Date.now(),
        });

        reject(error);
        commit("CLEAR_PENDING_CONNECTION_HANDLERS");
      }
    });
  },

  disconnect({ state, commit }) {
    if (state.wsInstance)
      state.wsInstance.close(1000, "User requested disconnection");

    commit("SET_WS_CONNECTED", false);
    commit("SET_WS_STATUS", "CLOSED");
  },

  reconnect({ state, dispatch }) {
    if (state.connectionOptions) {
      dispatch("connect", state.connectionOptions);
    }
  },

  sendMessage({ state, commit }, { message }) {
    if (!state.wsInstance || !state.wsConnected) return false;

    const messageToSend =
      typeof message === "object" ? JSON.stringify(message) : message;

    try {
      state.wsInstance.send(messageToSend);

      commit("ADD_DEBUGGING_MESSAGE", {
        type: "websocket-message-sent-debug",
        data: message,
        timestamp: Date.now(),
      });

      commit("ADD_MESSAGE", {
        type: "sent",
        data: message,
        timestamp: Date.now(),
      });

      return true;
    } catch (error) {
      commit("ADD_DEBUGGING_MESSAGE", {
        type: "websocket-message-sent-error-debug",
        data: error,
        timestamp: Date.now(),
      });

      return false;
    }
  },

  sendPing({ state, dispatch }) {
    if (!state.wsConnected) return false;

    const payload = {
      workspace_id: localStorage.getItem("currentWorkspace"),
      dialog_id: state.currentDialogId,
      type: "ping",
      timestamp: new Date().toISOString(),
    };

    return dispatch("sendMessage", { message: payload });
  },

  sendPingHTTP({ commit }, payload) {
    const postUrl = `/llm/crm/ping`;

    commit("ADD_DEBUGGING_MESSAGE", {
      type: "http-request-debug",
      data: payload,
      timestamp: Date.now(),
    });

    return new Promise((resolve, reject) => {
      imhubInstance()
        .post(postUrl, payload)
        .then((response) => {
          commit("ADD_MESSAGE", {
            type: "http-response",
            data: response.data,
            timestamp: Date.now(),
          });

          resolve(response);
        })
        .catch((err) => {
          commit("ADD_DEBUGGING_MESSAGE", {
            type: "http-request-error-debug",
            data: err,
            timestamp: Date.now(),
          });

          reject(err);
        });
    });
  },

  handleConnected({ commit }, websocket) {
    commit("ADD_DEBUGGING_MESSAGE", {
      type: "websocket-connected-debug",
      data: { message: "Conectado com sucesso", ...websocket },
      timestamp: Date.now(),
    });

    commit("SET_INVALID_STREAMING_TOKEN", false);

    commit("ADD_MESSAGE", {
      type: "system",
      data: { message: "Conectado com sucesso" },
      timestamp: Date.now(),
    });
  },

  handleDisconnected({ commit }, { websocket, event }) {
    const reason = event?.reason || "Sem motivo especificado";

    commit("ADD_DEBUGGING_MESSAGE", {
      type: "websocket-disconnected-debug",
      data: {
        message: "Desconectado",
        reason: reason,
        ...websocket,
      },
      timestamp: Date.now(),
    });

    if (reason === "Invalid streaming token" || reason.includes("Invalid")) {
      commit("SET_INVALID_STREAMING_TOKEN", true);
    }

    commit("ADD_MESSAGE", {
      type: "system",
      data: {
        message: "Desconectado",
        reason: reason,
      },
      timestamp: Date.now(),
    });
  },

  handleError({ commit }, { websocket, event }) {
    commit("ADD_DEBUGGING_MESSAGE", {
      type: "websocket-error-debug",
      data: { ...websocket, event },
      timestamp: Date.now(),
    });

    commit("ADD_MESSAGE", {
      type: "error",
      data: { message: "Erro na conexão WebSocket" },
      timestamp: Date.now(),
    });
  },

  handleMessage({ commit }, { data, event }) {
    commit("ADD_DEBUGGING_MESSAGE", {
      type: "websocket-message-received-debug",
      data: { ...data, event },
      timestamp: Date.now(),
    });

    if (data.type == "crm_coaching") {
      commit("SET_CURRENT_INSIGHTS", data);

      commit("ADD_INSIGHT_MESSAGE", {
        type: "received",
        data: data,
        timestamp: Date.now(),
      });
    }

    commit("ADD_MESSAGE", {
      type: "received",
      data: data,
      timestamp: Date.now(),
    });
  },
};

function getWebSocketUrl() {
  const streamingToken = localStorage.getItem("streamingToken");
  const username = localStorage.getItem("hubUsername");
  const baseWsUrl = process.env.VUE_APP_IMHUB_API_URL;

  if (!streamingToken || !username) return null;

  return `${baseWsUrl}ws/user/${username}/${streamingToken}?topics=crm_coaching`;
}

export default {
  namespaced: true,
  state: initialState(),
  getters,
  mutations,
  actions,
};
